import { CommonService } from '@services/common/CommonService';
import { ErrorToast } from '@services/utils/ToastUtils';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PwaService } from '../services/pwa/PWAService';
import { AppState } from '../store/AppState';
import { CurrentUser } from '../types';
import { AssertMetadataResponse } from '../types/pwa/AssertMetadataResponse';
import { MetadataAssertionResult } from '../types/pwa/MetadataAssertionResult';
import { RegisterPwaInstanceResponse } from '../types/pwa/RegisterPwaInstanceResponse';
import { useConfig } from './UseConfig';

export const usePwa = () => {
  const {
    firebaseApiKey,
    firebaseAuthDomain,
    firebaseProjectId,
    firebaseStorageBucket,
    firebaseMessagingSenderId,
    firebaseAppId,
    firebaseVapidKey,
  } = useConfig();
  // Initialize Firebase
  const firebaseConfig = {
    apiKey: firebaseApiKey,
    authDomain: firebaseAuthDomain,
    projectId: firebaseProjectId,
    storageBucket: firebaseStorageBucket,
    messagingSenderId: firebaseMessagingSenderId,
    appId: firebaseAppId,
  };

  const app = initializeApp(firebaseConfig);

  // Added below to check if the browser support the API's required to use the Firebase SDK.
  const messaging = (async () => {
    try {
      const isSupportedBrowser = await isSupported();
      if (isSupportedBrowser) {
        return getMessaging(app);
      }
      return null;
    } catch (err) {
      return null;
    }
  })();

  const currentUser = useSelector<AppState, CurrentUser>((state) => state.currentUser);

  const checkIfPwa = (): boolean => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    return false;
  };

  const [isPwa, setIsPwa] = useState<boolean>(checkIfPwa);

  useEffect(() => {
    setIsPwa(checkIfPwa);
  }, [window.matchMedia]);

  const setSelectedStore = (store) => {
    localStorage.setItem('am.storeCode', store.storeCode);

    CommonService.getHomePageOptionUrl().then((res) => {
      let homePageOptionUrl;
      !res ? (homePageOptionUrl = '/portfolio/all') : (homePageOptionUrl = res.toString());
      localStorage.setItem('homePageOptionUrl', homePageOptionUrl);
      return (window.location.href = homePageOptionUrl);
    });
  };

  const isPwaInstalled = () => {
    if ('serviceWorker' in navigator) {
      return navigator.serviceWorker.getRegistrations().then((registrations) => {
        return (
          registrations && registrations.find((x) => x.scope.includes(window.location.hostname))
        );
      });
    }
    return Promise.resolve(false);
  };

  const defaultRedirect = () => {
    if (currentUser.isPrimaryUser || currentUser.isMultiStoreUser) {
      window.location.href = '/home/select';
    } else {
      CommonService.getStores().then((stores) => {
        if (stores.length === 1) {
          setSelectedStore(stores[0]);
        } else {
          // pass in a query parameter so that the home select page knows not to get all dealers again
          window.location.href = '/home/select';
        }
      });
    }
  };

  const sendPwaRegistrationRequest = async (
    currentToken,
    pinCode,
    multiUserInstance,
    requestOffIpAccess,
    deviceInformation,
  ) => {
    try {
      const registerPwaInstanceResponse: RegisterPwaInstanceResponse = await PwaService.registerPwa(
        {
          fcmToken: currentToken,
          instanceSecret: 'instance_secret_test',
          pinCode: pinCode,
          multiUserInstance: multiUserInstance,
          requestOffIpAccess: requestOffIpAccess,
          deviceInformation: deviceInformation,
        },
      );

      if (registerPwaInstanceResponse.pwaInstanceId != null) {
        localStorage.setItem('pwa-instance-id', registerPwaInstanceResponse.pwaInstanceId);
        localStorage.setItem('fcmToken', currentToken);
      } else {
        throw Error('Error occurred while registering PWA instance.');
      }
    } catch (error) {
      ErrorToast(error?.message);
      throw error;
    }
  };

  const generateFcmToken = async () => {
    const messagingResolve = await messaging;
    let currentToken = null;

    let retries = 3;
    while (!currentToken && retries > 0) {
      currentToken = await getFCMToken(messagingResolve, retries);
      retries--;
    }

    return currentToken;
  };

  const registerPwaInstance = async (
    pinCode,
    multiUserInstance,
    requestOffIpAccess,
    deviceInformation = '',
  ) => {
    try {
      let currentToken = null;
      currentToken = await generateFcmToken();

      await sendPwaRegistrationRequest(
        currentToken,
        pinCode,
        multiUserInstance,
        requestOffIpAccess,
        deviceInformation,
      );
    } catch (err) {
      if (Notification.permission === 'denied') {
        await sendPwaRegistrationRequest(
          null,
          pinCode,
          multiUserInstance,
          requestOffIpAccess,
          deviceInformation,
        );
      } else {
        throw err;
      }
    }
  };

  const getFCMToken = async (messagingResolve, retries) => {
    let currentToken = null;
    try {
      currentToken = await getToken(messagingResolve, {
        vapidKey: firebaseVapidKey,
      });
    } catch (err) {
      if (retries === 1) {
        throw err;
      }
    }

    return currentToken;
  };

  const assertMetaData = async (deviceInformation?: string) => {
    try {
      const assertMetadataResponse: AssertMetadataResponse = await PwaService.assertMetaData(
        {
          fcmToken:
            localStorage.getItem('fcmToken') !== 'null' ? localStorage.getItem('fcmToken') : null,
          deviceInformation: deviceInformation,
        },
        localStorage.getItem('pwa-instance-id'),
      );

      return assertMetadataResponse.assertionResult;
    } catch (error) {
      return MetadataAssertionResult[MetadataAssertionResult.NotFound];
    }
  };

  const updateMetaData = async (deviceInformation) => {
    try {
      const updateMetadataResponse = await PwaService.updateMetaData(
        {
          fcmToken:
            localStorage.getItem('fcmToken') !== 'null' ? localStorage.getItem('fcmToken') : null,
          instanceSecret: 'instance_secret_test',
          deviceInformation: deviceInformation,
        },
        localStorage.getItem('pwa-instance-id'),
      );

      return updateMetadataResponse;
    } catch (error) {
      return false;
    }
  };

  const requestOffIpAccess = async () => {
    try {
      const requestOffResponse = await PwaService.requestOffIpAccess(
        localStorage.getItem('pwa-instance-id'),
      );

      return requestOffResponse;
    } catch (error) {
      return false;
    }
  };

  const getMobileEnabledFeatureToggle = async () => {
    try {
      const mobileEnabled = await PwaService.getMobileEnabledFeatureToggle();

      return mobileEnabled;
    } catch (error) {
      return false;
    }
  };

  const checkIfMobileOffIpAccessEnabled = async () => {
    try {
      const isMobileOffIpAccessEnabled = await PwaService.getMobileOffIpAccessActiveService();

      return isMobileOffIpAccessEnabled;
    } catch (error) {
      return false;
    }
  };

  const checkIfUserHasSingleDealerGroupAccess = async () => {
    try {
      const isMobileOffIpAccessEnabled = await PwaService.hasSingleDealerGroupAccess();

      return isMobileOffIpAccessEnabled;
    } catch (error) {
      return false;
    }
  };

  return {
    isPwa,
    isPwaInstalled,
    registerPwaInstance,
    assertMetaData,
    updateMetaData,
    requestOffIpAccess,
    defaultRedirect,
    sendPwaRegistrationRequest,
    generateFcmToken,
    getMobileEnabledFeatureToggle,
    checkIfMobileOffIpAccessEnabled,
    checkIfUserHasSingleDealerGroupAccess,
  };
};
